import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import FilterPanel from "../../../component/FilterPanel/FilterPanel";
import request from "../../../api/request";
import { Material } from "../../../const/model";
import { BackendAPI } from "../../../const/api";
import "./ListPage.css";
import { AuthContext } from "../../../auth/authContext";
import EditModal, {
  EditField,
  textFieldsToSelect,
} from "../../../component/EditModal/EditModal";
import { getLastSyncTime } from "../../../services";
import { useToast } from "../../../component/ToastBar";

const MaterialList = () => {
  const toast = useToast();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInitialStockModalOpen, setIsInitialStockModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [lastSyncTime, setLastSyncTime] = useState("");
  const [syncRes, setSyncRes] = useState<{
    created: number;
    updated: number;
    disabled: number;
    enabled: number;
  } | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const authContext = useContext(AuthContext);

  const fetchMaterials = useCallback(async () => {
    try {
      const params = {
        page: page + 1,
        size: rowsPerPage,
        sort: sortConfig.key ? `${sortConfig.key},${sortConfig.direction}` : "",
        ...filters,
      };
      const response = await request.get(BackendAPI.materialList, params);
      if (!response.success) {
        return;
      }
      setMaterials(response.data.materials);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error("Error fetching materials:", error);
    }
  }, [filters, page, rowsPerPage, sortConfig.direction, sortConfig.key]);

  const fetchLastSyncTime = async () => {
    const lastSyncTime = await getLastSyncTime("material");
    setLastSyncTime(lastSyncTime);
  };

  useEffect(() => {
    fetchLastSyncTime().then();
  }, []);

  useEffect(() => {
    fetchMaterials().then();
  }, [page, rowsPerPage, filters, fetchMaterials]);



  const handleSync = async () => {
    request.get(BackendAPI.syncMaterialList).then((res) => {
      if (res.success) {
        setSyncRes({
          created: res.data.created,
          updated: res.data.updated,
          disabled: res.data.disabled,
          enabled: res.data.enabled,
        });
        toast("同步成功");
        fetchLastSyncTime().then();
        fetchMaterials().then();
      }
    });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    // 重置到第一页，因为排序改变后应该从第一页开始显示
    setPage(0);
  };

  const handleEditClick = (material: Material) => {
    setEditData(material);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!authContext) {
      return;
    }
    if (
      authContext.isBA() ||
      authContext.isAdmin() ||
      authContext.isPurchaser()
    ) {
      await request
        .put(BackendAPI.updateMaterial(editData.id), editData)
        .then((res) => {
          if (!res.success) {
            return;
          }
          toast("更新成功");
          handleCloseEdit();
          fetchMaterials().then();
        });
    } else {
      toast("无权限", "error");
      handleCloseEdit();
    }
  };

  const handleCloseEdit = () => {
    setEditData({});
    setIsEditModalOpen(false);
  };

  const handleDisableMaterial = async (material: Material) => {
    try {
      const res = await request.post(
        BackendAPI.disableMaterial(material.id.toString()),
        {},
      );
      if (res.success) {
        toast("禁用成功");
        fetchMaterials().then();
      } else {
        toast(res.message || "禁用失败", "error");
      }
    } catch (error) {
      console.error("Error disabling material:", error);
      toast("禁用失败", "error");
    }
  };

  const handleEnableMaterial = async (material: Material) => {
    try {
      const res = await request.post(
        BackendAPI.enableMaterial(material.id.toString()),
        {},
      );
      if (res.success) {
        toast("启用成功");
        fetchMaterials().then();
      } else {
        toast(res.message || "启用失败", "error");
      }
    } catch (error) {
      console.error("Error enabling material:", error);
      toast("启用失败", "error");
    }
  };

  const fields = () => {
    const editLabels: EditField[] = [];
    editLabels.push(
      {
        label: "物料编码",
        type: "text",
        value: editData.code,
        onChange: (data) => {
          setEditData({
            ...editData,
            code: data,
          });
        },
      },
      {
        label: "物料名称",
        type: "text",
        value: editData.name,
        onChange: (data) => {
          setEditData({
            ...editData,
            name: data,
          });
        },
      },
      {
        label: "物料型号",
        type: "text",
        value: editData.model,
        onChange: (data) => {
          setEditData({
            ...editData,
            model: data,
          });
        },
      },
      {
        label: "基本单位",
        type: "text",
        value: editData.unit,
        onChange: (data) => {
          setEditData({
            ...editData,
            unit: data,
          });
        },
      },
      {
        label: "来源",
        type: "select",
        options: textFieldsToSelect(["外购", "自制"]),
        value: editData.source,
        onChange: (data) => {
          setEditData({
            ...editData,
            source: data,
          });
        },
      },
      {
        label: "存货类别",
        type: "text",
        value: editData.category,
        onChange: (data) => {
          setEditData({
            ...editData,
            category: data,
          });
        },
      },
      {
        label: "是否网销",
        type: "switch",
        value: editData.onlineSales,
        onChange: (data) => {
          setEditData({
            ...editData,
            onlineSales: data,
          });
        },
      },
      {
        label: "已使用",
        type: "switch",
        value: editData.used,
        onChange: (data) => {
          setEditData({
            ...editData,
            used: data,
          });
        },
      },
      {
        label: "备注",
        type: "text",
        value: editData.note,
        onChange: (data) => {
          setEditData({
            ...editData,
            note: data,
          });
        },
      },
    );
    return editLabels;
  };

  const handleInitialStockUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await request.post(
        BackendAPI.previewStockUpdate,
        null,
        formData,
      );
      if (response.success) {
        setPreviewData(response.data);
        setIsInitialStockModalOpen(true);
      } else {
        toast(response.message || "预览失败", "error");
      }
    } catch (error) {
      toast("预览失败", "error");
    }
  };

  const handleConfirmInitialStock = async () => {
    try {
      const response = await request.post(BackendAPI.confirmStockUpdate, {
        preview_data: previewData,
      });
      if (response.success) {
        toast("期初库存更新成功");
        setIsInitialStockModalOpen(false);
        setPreviewData([]);
        fetchMaterials();
      } else {
        toast(response.message || "更新失败", "error");
      }
    } catch (error) {
      console.error("Error confirming initial stock:", error);
      toast("更新失败", "error");
    }
  };

  const handleDownloadMaterials = async () => {
    try {
      const response = await request.get(BackendAPI.downloadMaterialList);

      // 检查响应是否为blob（文件）
      if (response instanceof Blob) {
        // 创建下载链接
        const url = window.URL.createObjectURL(response);
        window.open(url, "_blank");
        toast("物料列表下载成功");
      } else {
        // 如果不是blob，说明是错误响应
        toast(response?.message || "下载失败", "error");
      }
    } catch (error) {
      console.error("Error downloading materials:", error);
      toast("下载失败", "error");
    }
  };

  return (
    <Paper>
      <FilterPanel
        filterOptions={[
          { key: "query", label: "搜索（编码/名称/型号）", type: "text" },
          { 
            key: "disabled", 
            label: "禁用状态", 
            type: "select",
            options: [
              {
                label: "全部",
                value: "",
              },
              {
                label: "启用",
                value: "false",
              },
              {
                label: "禁用",
                value: "true",
              },
            ]
          },
        ]}
        onFilterChange={handleFilterChange}
      />
      <div style={{ padding: "16px" }}>
        {(authContext?.isBA() || authContext?.isAdmin()) && (
          <div>
            <Button onClick={handleSync}>同步金蝶物料列表</Button>
            {syncRes && (
              <span>
                本次：创建{`${syncRes.created}`}条, 更新{`${syncRes.updated}`}
                条, 禁用{`${syncRes.disabled}`}条, 启用{`${syncRes.enabled}`}条
              </span>
            )}
            <div />
            {lastSyncTime ? (
              <span>上次同步时间: {lastSyncTime}</span>
            ) : (
              <span>上次同步时间: 从未同步</span>
            )}
          </div>
        )}
        {(authContext?.isWarehouse() || authContext?.isAdmin()) && (
          <div style={{ marginTop: "1px" }}>
            <input
              accept=".xlsx"
              style={{ display: "none" }}
              id="initial-stock-upload"
              type="file"
              onChange={handleInitialStockUpload}
            />
            <label htmlFor="initial-stock-upload">
              <Button
                variant="contained"
                component="span"
                style={{ marginRight: "8px" }}
              >
                更新期初库存
              </Button>
            </label>
            <Button
              variant="outlined"
              onClick={handleDownloadMaterials}
              style={{ marginLeft: "8px" }}
            >
              下载物料列表
            </Button>
            <p>
              请使用以下模板文件进行库存更新：
              <a
                href="/templates/库存更新模板.xlsx"
                download
                className="download-template-link"
              >
                下载库存更新模板
              </a>
            </p>
          </div>
        )}
      </div>
      <Table className="list-table">
        <TableHead>
          <TableRow>
            <TableCell className="sortable-column column-width-small">
              操作
            </TableCell>
            <TableCell
              className="sortable-column column-width-small"
              onClick={() => handleSort("id")}
            >
              物料ID
            </TableCell>
            <TableCell
              className="sortable-column column-width-small"
              onClick={() => handleSort("code")}
            >
              物料编码
            </TableCell>
            <TableCell
              className="sortable-column column-width-small"
              onClick={() => handleSort("name")}
            >
              物料名
            </TableCell>
            <TableCell
              className="sortable-column column-width-medium"
              onClick={() => handleSort("model")}
            >
              型号规格
            </TableCell>
            <TableCell
              className="sortable-column column-width-small"
              onClick={() => handleSort("category")}
            >
              存货类别
            </TableCell>
            <TableCell
              className="sortable-column column-width-small"
              onClick={() => handleSort("status")}
            >
              状态
            </TableCell>
            <TableCell
              className="sortable-column column-width-small"
              onClick={() => handleSort("current_stock")}
            >
              当前库存
            </TableCell>
            <TableCell
              className="sortable-column column-width-small"
              onClick={() => handleSort("last_stock_update_at")}
            >
              最后更新时间
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {materials.map((material) => (
            <TableRow key={material.id}>
              <TableCell>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <Button
                    disabled={
                      authContext &&
                      !authContext.isBA() &&
                      !authContext.isAdmin() &&
                      !authContext.isPurchaser()
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(material);
                    }}
                    size="small"
                    variant="outlined"
                  >
                    编辑
                  </Button>
                  {(authContext?.isBA() ||
                    authContext?.isAdmin() ||
                    authContext?.isWarehouse()) && (
                    <>
                      {!material.disabled ? (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisableMaterial(material);
                          }}
                          size="small"
                          variant="outlined"
                          color="error"
                        >
                          禁用
                        </Button>
                      ) : (
                        (authContext?.isBA() ||
                          authContext?.isAdmin() ||
                          authContext?.isWarehouse()) && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEnableMaterial(material);
                            }}
                            size="small"
                            variant="outlined"
                            color="success"
                          >
                            启用
                          </Button>
                        )
                      )}
                    </>
                  )}
                </div>
              </TableCell>
              <TableCell>{material.id}</TableCell>
              <TableCell>{material.code}</TableCell>
              <TableCell>{material.name}</TableCell>
              <TableCell>{material.model}</TableCell>
              <TableCell>{material.category}</TableCell>
              <TableCell>{material.status}</TableCell>
              <TableCell>{material.currentStock}</TableCell>
              <TableCell>
                {material.lastStockUpdateAt
                  ? new Date(material.lastStockUpdateAt).toLocaleString()
                  : "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) =>
          setRowsPerPage(parseInt(event.target.value, 10))
        }
      />
      {isEditModalOpen && (
        <EditModal
          fields={fields()}
          onSave={handleSaveEdit}
          onClose={handleCloseEdit}
        />
      )}
      {isInitialStockModalOpen && (
        <Dialog
          open={isInitialStockModalOpen}
          onClose={() => setIsInitialStockModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>确认期初库存更新</DialogTitle>
          <DialogContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>物料编码</TableCell>
                  <TableCell>物料名称</TableCell>
                  <TableCell>型号规格</TableCell>
                  <TableCell>当前库存</TableCell>
                  <TableCell>新库存</TableCell>
                  <TableCell>更新日期</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.code}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.model}</TableCell>
                    <TableCell>{item.currentStock}</TableCell>
                    <TableCell>{item.newStock}</TableCell>
                    <TableCell>{item.updateDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsInitialStockModalOpen(false)}>
              取消
            </Button>
            <Button onClick={handleConfirmInitialStock} variant="contained">
              确认更新
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
};

export default MaterialList;
